import { useCallback, useState } from 'react'
import { importLibrary } from '@googlemaps/js-api-loader'

import { useRealtimeStore } from '../store/realtime-store'
import type { Job } from '../types'
import type { LatLng } from '../utils/distance'
import { normalizeAddressKey, readGeocodeCache, writeGeocodeCache } from '../utils/geocode-cache'

export type GeocodeState = 'pending' | 'failed'

/** One in-flight request per address, shared across callers. */
const inFlight = new Map<string, Promise<LatLng | null>>()

async function geocodeAddress(address: string): Promise<LatLng | null> {
  const key = normalizeAddressKey(address)
  const existing = inFlight.get(key)
  if (existing) return existing

  const request = (async () => {
    try {
      await importLibrary('geocoding')
      const { results } = await new google.maps.Geocoder().geocode({ address })
      const location = results?.[0]?.geometry?.location
      if (!location) throw new Error('ZERO_RESULTS')
      const coords = { lat: location.lat(), lng: location.lng() }
      writeGeocodeCache(address, { status: 'ok', ...coords, at: Date.now() })
      return coords
    } catch (error) {
      writeGeocodeCache(address, { status: 'failed', at: Date.now(), error: error instanceof Error ? error.message : String(error) })
      return null
    } finally {
      inFlight.delete(key)
    }
  })()

  inFlight.set(key, request)
  return request
}

/**
 * On-demand geocoding for jobs whose address has no coordinates. Results are cached
 * (memory + localStorage) and patched into the store so the marker appears immediately.
 * Requires the Geocoding API to be enabled for the Maps key.
 */
export function useJobGeocoder() {
  const [stateByJobId, setStateByJobId] = useState<Record<string, GeocodeState>>({})

  const geocodeJob = useCallback(async (job: Job, { force = false }: { force?: boolean } = {}): Promise<LatLng | null> => {
    const address = job.address?.trim()
    if (!address) return null

    const cached = readGeocodeCache(address)
    if (cached?.status === 'ok') {
      useRealtimeStore.getState().applyEvents([{ type: 'job.updated', job: { id: job.id, lat: cached.lat, lng: cached.lng } }])
      return { lat: cached.lat, lng: cached.lng }
    }
    if (cached?.status === 'failed' && !force) {
      setStateByJobId((current) => ({ ...current, [job.id]: 'failed' }))
      return null
    }

    setStateByJobId((current) => ({ ...current, [job.id]: 'pending' }))
    const coords = await geocodeAddress(address)
    setStateByJobId((current) => {
      const next = { ...current }
      if (coords) delete next[job.id]
      else next[job.id] = 'failed'
      return next
    })
    if (coords) {
      useRealtimeStore.getState().applyEvents([{ type: 'job.updated', job: { id: job.id, lat: coords.lat, lng: coords.lng } }])
    }
    return coords
  }, [])

  return { geocodeJob, stateByJobId }
}
